import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "../ui/use-toast";
import { useTranslation } from "react-i18next";
import type { OrderPayload } from "../../types/api";

interface OrderPanelProps {
  symbol: string;
  currentPrice: number;
  availableBase?: number;
  availableQuote?: number;
  onOrderSubmit?: (order: OrderPayload) => void;
}

/**
 * Composant OrderPanel - Panneau d'ordre pour BUY/SELL
 */
export const OrderPanel = ({
  symbol,
  currentPrice,
  availableBase = 0,
  availableQuote = 0,
  onOrderSubmit,
}: OrderPanelProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [orderType, setOrderType] = useState<"MARKET" | "LIMIT" | "STOP_LIMIT">(
    "MARKET"
  );
  const [buyQuantity, setBuyQuantity] = useState("");
  const [sellQuantity, setSellQuantity] = useState("");
  const [buyPrice, setBuyPrice] = useState(currentPrice.toFixed(2));
  const [sellPrice, setSellPrice] = useState(currentPrice.toFixed(2));
  const [buyStopPrice, setBuyStopPrice] = useState(currentPrice.toFixed(2));
  const [sellStopPrice, setSellStopPrice] = useState(currentPrice.toFixed(2));
  const [buyStopLoss, setBuyStopLoss] = useState("");
  const [buyTakeProfit, setBuyTakeProfit] = useState("");
  const [sellStopLoss, setSellStopLoss] = useState("");
  const [sellTakeProfit, setSellTakeProfit] = useState("");
  const [buySlider, setBuySlider] = useState(0);
  const [sellSlider, setSellSlider] = useState(0);
  const hasManualPriceRef = useRef(false);

  const baseAsset = symbol.split("/")[0];
  const quoteAsset = symbol.split("/")[1] ?? "USD";

  useEffect(() => {
    if (hasManualPriceRef.current) {
      return;
    }
    const formatted = currentPrice.toFixed(2);
    setBuyPrice(formatted);
    setSellPrice(formatted);
    setBuyStopPrice(formatted);
    setSellStopPrice(formatted);
  }, [currentPrice]);

  const buyPriceNumber =
    orderType === "MARKET" ? currentPrice : parseFloat(buyPrice);
  const sellPriceNumber =
    orderType === "MARKET" ? currentPrice : parseFloat(sellPrice);
  const feeRate = 0.001;

  const buyTotal = useMemo(() => {
    const qty = parseFloat(buyQuantity);
    const price = Number.isFinite(buyPriceNumber) ? buyPriceNumber : 0;
    if (!Number.isFinite(qty) || !Number.isFinite(price)) return "0.00";
    return (qty * price).toFixed(2);
  }, [buyPriceNumber, buyQuantity]);

  const sellTotal = useMemo(() => {
    const qty = parseFloat(sellQuantity);
    const price = Number.isFinite(sellPriceNumber) ? sellPriceNumber : 0;
    if (!Number.isFinite(qty) || !Number.isFinite(price)) return "0.00";
    return (qty * price).toFixed(2);
  }, [sellPriceNumber, sellQuantity]);

  const buyFee = useMemo(() => {
    const total = Number.parseFloat(buyTotal);
    if (!Number.isFinite(total)) return "0.00";
    return (total * feeRate).toFixed(2);
  }, [buyTotal, feeRate]);

  const sellFee = useMemo(() => {
    const total = Number.parseFloat(sellTotal);
    if (!Number.isFinite(total)) return "0.00";
    return (total * feeRate).toFixed(2);
  }, [sellTotal, feeRate]);

  const buyImpact = orderType === "MARKET" ? "0.05%" : "0.00%";
  const sellImpact = orderType === "MARKET" ? "0.05%" : "0.00%";

  const handleSubmit = (e: FormEvent, orderSide: "BUY" | "SELL") => {
    e.preventDefault();

    const quantity = orderSide === "BUY" ? buyQuantity : sellQuantity;
    const price = orderSide === "BUY" ? buyPrice : sellPrice;
    const stopPrice = orderSide === "BUY" ? buyStopPrice : sellStopPrice;
    const stopLoss = orderSide === "BUY" ? buyStopLoss : sellStopLoss;
    const takeProfit = orderSide === "BUY" ? buyTakeProfit : sellTakeProfit;

    if (!quantity || parseFloat(quantity) <= 0) {
      toast({
        title: t("trading.orderPanelErrorTitle"),
        description: t("trading.orderPanelErrorInvalidQuantity"),
        variant: "destructive",
      });
      return;
    }

    const order: OrderPayload = {
      symbol,
      side: orderSide,
      type: orderType,
      quantity: parseFloat(quantity),
      price:
        orderType === "LIMIT" || orderType === "STOP_LIMIT"
          ? parseFloat(price)
          : undefined,
      stopPrice: orderType === "STOP_LIMIT" ? parseFloat(stopPrice) : undefined,
      stopLoss: stopLoss ? parseFloat(stopLoss) : undefined,
      takeProfit: takeProfit ? parseFloat(takeProfit) : undefined,
    };

    onOrderSubmit?.(order);

    const priceText =
      orderType === "MARKET"
        ? t("trading.orderPanelOrderSubmittedMarket")
        : `$${price}`;
    toast({
      title: t("trading.orderPanelOrderSubmittedTitle"),
      description: t("trading.orderPanelOrderSubmittedDescription", {
        side: orderSide,
        quantity,
        asset: symbol.split("/")[0],
        price: priceText,
      }),
      variant: "success",
    });

    // Réinitialiser le formulaire
    setBuyQuantity("");
    setSellQuantity("");
    setBuySlider(0);
    setSellSlider(0);
    setBuyStopLoss("");
    setBuyTakeProfit("");
    setSellStopLoss("");
    setSellTakeProfit("");
    hasManualPriceRef.current = false;
  };

  return (
    <div className="h-full w-full rounded-2xl border border-border bg-card bg-gradient-to-br from-card to-muted/30 p-4 shadow-md shadow-slate-900/5 backdrop-blur-sm dark:border-white/5 dark:from-slate-900/50 dark:to-slate-800/30 dark:shadow-lg dark:shadow-black/20">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground dark:text-white">
          {t("trading.orderPanelTitle")}
        </h3>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {currentPrice.toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 8,
          })}
        </p>
      </div>

      <div className="mb-4 flex gap-2">
        <Button
          type="button"
          variant={orderType === "MARKET" ? "default" : "ghost"}
          onClick={() => setOrderType("MARKET")}
          className={`flex-1 transition-all duration-200 ${
            orderType === "MARKET"
              ? "bg-gradient-to-r from-cyan-400/90 to-cyan-500/90 text-black shadow-md shadow-cyan-500/20 hover:from-cyan-400 hover:to-cyan-500"
              : "border border-border bg-muted/60 backdrop-blur-sm hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:hover:shadow-sm"
          }`}>
          {t("trading.orderPanelMarketTab")}
        </Button>
        <Button
          type="button"
          variant={orderType === "LIMIT" ? "default" : "ghost"}
          onClick={() => setOrderType("LIMIT")}
          className={`flex-1 transition-all duration-200 ${
            orderType === "LIMIT"
              ? "bg-gradient-to-r from-cyan-400/90 to-cyan-500/90 text-black shadow-md shadow-cyan-500/20 hover:from-cyan-400 hover:to-cyan-500"
              : "border border-border bg-muted/60 backdrop-blur-sm hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:hover:shadow-sm"
          }`}>
          {t("trading.orderPanelLimitTab")}
        </Button>
        <Button
          type="button"
          variant={orderType === "STOP_LIMIT" ? "default" : "ghost"}
          onClick={() => setOrderType("STOP_LIMIT")}
          className={`flex-1 transition-all duration-200 ${
            orderType === "STOP_LIMIT"
              ? "bg-gradient-to-r from-cyan-400/90 to-cyan-500/90 text-black shadow-md shadow-cyan-500/20 hover:from-cyan-400 hover:to-cyan-500"
              : "border border-border bg-muted/60 backdrop-blur-sm hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 dark:hover:shadow-sm"
          }`}>
          {t("trading.orderPanelStopLimitTab")}
        </Button>
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <form
          onSubmit={(e) => handleSubmit(e, "BUY")}
          className="rounded-2xl border border-emerald-500/30 bg-emerald-50/50 p-4 dark:border-emerald-500/20 dark:bg-gradient-to-br dark:from-emerald-950/40 dark:to-slate-900/40">
          <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground dark:text-white/60">
            <span className="rounded-full bg-emerald-500/15 px-2 py-1 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
              {orderType === "MARKET"
                ? t("trading.orderPanelMarketTab")
                : t("trading.orderPanelLimitTab")}
            </span>
            <span>{quoteAsset}</span>
          </div>
          {orderType !== "MARKET" && (
            <div className="space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelPriceLabel")}
              </Label>
              <Input
                type="number"
                value={buyPrice}
                onChange={(e) => {
                  hasManualPriceRef.current = true;
                  setBuyPrice(e.target.value);
                }}
                placeholder={currentPrice.toFixed(2)}
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-emerald-500 focus-visible:ring-emerald-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-emerald-400/50"
              />
            </div>
          )}
          {orderType === "STOP_LIMIT" && (
            <div className="mt-3 space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelStopPriceLabel")}
              </Label>
              <Input
                type="number"
                value={buyStopPrice}
                onChange={(e) => {
                  hasManualPriceRef.current = true;
                  setBuyStopPrice(e.target.value);
                }}
                placeholder={currentPrice.toFixed(2)}
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-emerald-500 focus-visible:ring-emerald-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-emerald-400/50"
              />
            </div>
          )}
          <div className="mt-3 space-y-2">
            <Label className="text-foreground dark:text-white/80">
              {t("trading.orderPanelQuantityLabel")} ({baseAsset})
            </Label>
            <Input
              type="number"
              value={buyQuantity}
              onChange={(e) => setBuyQuantity(e.target.value)}
              placeholder="0.00000000"
              step="any"
              min="0"
              className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-emerald-500 focus-visible:ring-emerald-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-emerald-400/50"
            />
            <p className="text-xs text-muted-foreground dark:text-white/50">
              {t("trading.orderPanelAvailableLabel")}:{" "}
              {availableQuote.toFixed(2)} {quoteAsset}
            </p>
          </div>
          <div className="mt-3 space-y-2">
            <Label className="text-foreground dark:text-white/80">
              {t("trading.orderPanelTotalLabel")} ({quoteAsset})
            </Label>
            <Input
              type="text"
              value={buyTotal}
              readOnly
              className="border-border bg-muted/50 text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/70"
            />
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelStopLossLabel")}
              </Label>
              <Input
                type="number"
                value={buyStopLoss}
                onChange={(e) => setBuyStopLoss(e.target.value)}
                placeholder="0.00000000"
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-emerald-500 focus-visible:ring-emerald-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-emerald-400/50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelTakeProfitLabel")}
              </Label>
              <Input
                type="number"
                value={buyTakeProfit}
                onChange={(e) => setBuyTakeProfit(e.target.value)}
                placeholder="0.00000000"
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-emerald-500 focus-visible:ring-emerald-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-emerald-400/50"
              />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <input
              type="range"
              min={0}
              max={100}
              value={buySlider}
              onChange={(e) => {
                const next = Number(e.target.value);
                setBuySlider(next);
                const amount = (availableQuote * next) / 100;
                const price = Number.isFinite(buyPriceNumber)
                  ? buyPriceNumber
                  : currentPrice;
                const qty = price > 0 ? amount / price : 0;
                setBuyQuantity(qty > 0 ? qty.toFixed(8) : "");
              }}
              className="w-full"
            />
            <div className="text-xs text-muted-foreground dark:text-white/60">
              {t("trading.orderPanelTotalLabel")}: {buyTotal} {quoteAsset}
            </div>
            <div className="text-xs text-muted-foreground dark:text-white/60">
              {t("trading.orderPanelFeeLabel")}: {buyFee} {quoteAsset} (
              {(feeRate * 100).toFixed(2)}%)
            </div>
            <div className="text-xs text-muted-foreground dark:text-white/60">
              {t("trading.orderPanelImpactLabel")}: {buyImpact}
            </div>
          </div>
          <Button
            type="submit"
            className="mt-4 w-full rounded-full bg-gradient-to-r from-emerald-500/90 to-green-500/90 py-3 text-base font-semibold shadow-lg shadow-emerald-500/20 transition-all duration-200 hover:from-emerald-500 hover:to-green-500 hover:shadow-xl hover:shadow-emerald-500/30">
            {t("trading.orderPanelBuyCta", { asset: baseAsset })}
          </Button>
        </form>

        <form
          onSubmit={(e) => handleSubmit(e, "SELL")}
          className="rounded-2xl border border-rose-500/30 bg-rose-50/50 p-4 dark:border-rose-500/20 dark:bg-gradient-to-br dark:from-rose-950/40 dark:to-slate-900/40">
          <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground dark:text-white/60">
            <span className="rounded-full bg-rose-500/15 px-2 py-1 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200">
              {orderType === "MARKET"
                ? t("trading.orderPanelMarketTab")
                : t("trading.orderPanelLimitTab")}
            </span>
            <span>{baseAsset}</span>
          </div>
          {orderType !== "MARKET" && (
            <div className="space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelPriceLabel")}
              </Label>
              <Input
                type="number"
                value={sellPrice}
                onChange={(e) => {
                  hasManualPriceRef.current = true;
                  setSellPrice(e.target.value);
                }}
                placeholder={currentPrice.toFixed(2)}
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-rose-500 focus-visible:ring-rose-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-rose-400/50"
              />
            </div>
          )}
          {orderType === "STOP_LIMIT" && (
            <div className="mt-3 space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelStopPriceLabel")}
              </Label>
              <Input
                type="number"
                value={sellStopPrice}
                onChange={(e) => {
                  hasManualPriceRef.current = true;
                  setSellStopPrice(e.target.value);
                }}
                placeholder={currentPrice.toFixed(2)}
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-rose-500 focus-visible:ring-rose-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-rose-400/50"
              />
            </div>
          )}
          <div className="mt-3 space-y-2">
            <Label className="text-foreground dark:text-white/80">
              {t("trading.orderPanelQuantityLabel")} ({baseAsset})
            </Label>
            <Input
              type="number"
              value={sellQuantity}
              onChange={(e) => setSellQuantity(e.target.value)}
              placeholder="0.00000000"
              step="any"
              min="0"
              className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-rose-500 focus-visible:ring-rose-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-rose-400/50"
            />
            <p className="text-xs text-muted-foreground dark:text-white/50">
              {t("trading.orderPanelAvailableLabel")}:{" "}
              {availableBase.toFixed(8)} {baseAsset}
            </p>
          </div>
          <div className="mt-3 space-y-2">
            <Label className="text-foreground dark:text-white/80">
              {t("trading.orderPanelTotalLabel")} ({quoteAsset})
            </Label>
            <Input
              type="text"
              value={sellTotal}
              readOnly
              className="border-border bg-muted/50 text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/70"
            />
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelStopLossLabel")}
              </Label>
              <Input
                type="number"
                value={sellStopLoss}
                onChange={(e) => setSellStopLoss(e.target.value)}
                placeholder="0.00000000"
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-rose-500 focus-visible:ring-rose-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-rose-400/50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground dark:text-white/80">
                {t("trading.orderPanelTakeProfitLabel")}
              </Label>
              <Input
                type="number"
                value={sellTakeProfit}
                onChange={(e) => setSellTakeProfit(e.target.value)}
                placeholder="0.00000000"
                step="any"
                min="0"
                className="border-border bg-background text-foreground placeholder:text-muted-foreground backdrop-blur-sm transition-all focus-visible:border-rose-500 focus-visible:ring-rose-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus-visible:border-rose-400/50"
              />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <input
              type="range"
              min={0}
              max={100}
              value={sellSlider}
              onChange={(e) => {
                const next = Number(e.target.value);
                setSellSlider(next);
                const qty = (availableBase * next) / 100;
                setSellQuantity(qty > 0 ? qty.toFixed(8) : "");
              }}
              className="w-full"
            />
            <div className="text-xs text-muted-foreground dark:text-white/60">
              {t("trading.orderPanelTotalLabel")}: {sellTotal} {quoteAsset}
            </div>
            <div className="text-xs text-muted-foreground dark:text-white/60">
              {t("trading.orderPanelFeeLabel")}: {sellFee} {quoteAsset} (
              {(feeRate * 100).toFixed(2)}%)
            </div>
            <div className="text-xs text-muted-foreground dark:text-white/60">
              {t("trading.orderPanelImpactLabel")}: {sellImpact}
            </div>
          </div>
          <Button
            type="submit"
            variant="destructive"
            className="mt-4 w-full rounded-full bg-gradient-to-r from-rose-500/90 to-red-500/90 py-3 text-base font-semibold shadow-lg shadow-rose-500/20 transition-all duration-200 hover:from-rose-500 hover:to-red-500 hover:shadow-xl hover:shadow-rose-500/30">
            {t("trading.orderPanelSellCta", { asset: baseAsset })}
          </Button>
        </form>
      </div>
    </div>
  );
};
